import {
    HiOutlineCheck,
    HiOutlineUserAdd,
    HiOutlineChevronDown,
} from 'react-icons/hi'
import { Button, Badge, Tooltip, Dropdown } from 'components/ui'
import { inventoryStatusColor } from 'utils/storeHube/statusColor'

const CLOSED = 4

/* --------------------------------------------------------------------------
 * StatusChip — chip visual del estado (sin interaccion).
 * Sirve como contenido del trigger del dropdown y como fallback cuando el
 * usuario no tiene permiso para cambiar de estado.
 * -------------------------------------------------------------------------- */
export const StatusChip = ({ statusId }) => {
    const meta = inventoryStatusColor[statusId]
    if (!meta) return null
    return (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[13px] font-medium leading-tight whitespace-nowrap bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
            <Badge innerClass={meta.dotClass} />
            {meta.label}
        </span>
    )
}

/* --------------------------------------------------------------------------
 * StatusDropdown — chip de estado clickeable con menu de transiciones.
 *
 * Render:
 *   - Sin permiso (`!canChangeStatus`) o sin opciones validas: chip plano (read-only).
 *   - Con permiso: chip + chevron envuelto en Dropdown. El menu lista
 *       1) Estado actual (disabled, marcado "Actual")
 *       2) Transicion sugerida (`nextStatus`) si existe y no es la actual
 *
 * "Cancelado" se quito a pedido del PO (2026-05): la transicion a CANCELLED
 * se maneja por otro flujo y no se expone como opcion rapida en el chip.
 *
 * `data-clarity-event="status-change"` permite a Clarity correlacionar
 * con los rage-clicks que motivaron el rediseño.
 * -------------------------------------------------------------------------- */
export const StatusDropdown = ({
    statusId,
    nextStatus,
    canChangeStatus,
    loading,
    onChangeStatus,
    t,
}) => {
    const meta = inventoryStatusColor[statusId]
    if (!meta) return null

    const nextMeta = nextStatus?.id ? inventoryStatusColor[nextStatus.id] : null

    const options = []
    if (nextStatus?.id && nextStatus.id !== statusId) {
        options.push({
            id: nextStatus.id,
            label: nextMeta?.label || nextStatus.label,
            dotClass: nextMeta?.dotClass || nextStatus.color,
        })
    }

    if (!canChangeStatus || options.length === 0) {
        return <StatusChip statusId={statusId} />
    }

    // Trigger: chevron integrado dentro del chip pill para que se lea como
    // un solo control. El padding-derecho lo dejamos un poco mas chico que
    // el izquierdo para que el chevron no se vea desplazado.
    const trigger = (
        <button
            type="button"
            data-clarity-event="status-change"
            disabled={loading}
            className="inline-flex items-center gap-1.5 pl-2 pr-1.5 py-1 rounded-md text-[13px] font-medium leading-tight whitespace-nowrap bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label={t('change_status', 'Cambiar estado')}
        >
            <Badge innerClass={meta.dotClass} />
            <span>{meta.label}</span>
            <HiOutlineChevronDown
                className="w-3 h-3 text-gray-500 dark:text-gray-300 opacity-70"
                aria-hidden="true"
            />
        </button>
    )

    return (
        <Dropdown trigger={['click', 'hover']} placement="bottom-end" renderTitle={trigger}>
            <Dropdown.Item disabled eventKey={`current-${statusId}`}>
                <div className="flex items-center gap-2 w-full opacity-60">
                    <Badge innerClass={meta.dotClass} />
                    <span>{meta.label}</span>
                </div>
            </Dropdown.Item>
            {options.map((opt) => (
                <Dropdown.Item
                    key={opt.id}
                    eventKey={`status-${opt.id}`}
                    onSelect={() => onChangeStatus?.(opt.id)}
                >
                    <div className="flex items-center gap-2">
                        <Badge innerClass={opt.dotClass} />
                        <span>{opt.label}</span>
                    </div>
                </Dropdown.Item>
            ))}
        </Dropdown>
    )
}

/* --------------------------------------------------------------------------
 * CtaGroup — botones de accion (Asignar / Solucionar)
 * `block` = ocupa el ancho completo (barra mobile fixed-bottom)
 * -------------------------------------------------------------------------- */
export const CtaGroup = ({
    isInformative,
    isLegacy,
    noResponsible,
    ctaSolveLabel,
    canChangeStatus,
    canUpdate,
    loading,
    onAssign,
    onSolve,
    t,
    block,
}) => {
    // Hallazgo legacy (sin ticket_category.type ni question_group.action ni
    // incidence): no exponemos botones porque no hay flujo de resolucion definido.
    // El usuario puede cambiar estado via el dropdown del chip si tiene permiso.
    if (isLegacy) return null

    const wrapClass = block ? 'flex w-full gap-2' : 'flex items-center gap-2 flex-shrink-0'
    const btnExtra  = block ? 'flex-1' : ''
    const solveBtnClass = `${btnExtra} !bg-indigo-500 hover:!bg-indigo-600 !border-indigo-500 hover:!border-indigo-600 !text-white`

    if (isInformative) {
        return (
            <div className={wrapClass}>
                <Button
                    size="sm"
                    variant="solid"
                    icon={<HiOutlineCheck />}
                    loading={loading}
                    disabled={!canChangeStatus}
                    onClick={() => onSolve?.(CLOSED)}
                    className={solveBtnClass}
                    block={block}
                >
                    {t('mark_as_read', 'Marcar como leído')}
                </Button>
            </div>
        )
    }

    if (noResponsible) {
        return (
            <div className={wrapClass}>
                <Button
                    size="sm"
                    variant="default"
                    icon={<HiOutlineUserAdd />}
                    disabled={!canUpdate}
                    onClick={onAssign}
                    className={btnExtra}
                    block={block}
                >
                    {t('assign_responsible', 'Asignar responsable')}
                </Button>
                <Button
                    size="sm"
                    variant="solid"
                    icon={<HiOutlineCheck />}
                    disabled={!canChangeStatus}
                    loading={loading}
                    onClick={() => onSolve?.(CLOSED)}
                    className={solveBtnClass}
                    block={block}
                >
                    {ctaSolveLabel}
                </Button>
            </div>
        )
    }

    return (
        <div className={wrapClass}>
            <Tooltip title={t('change_assignment', 'Cambiar asignación')}>
                <Button
                    size="sm"
                    variant="default"
                    icon={<HiOutlineUserAdd />}
                    disabled={!canUpdate}
                    onClick={onAssign}
                    className={btnExtra}
                    block={block}
                >
                    {t('assign', 'Asignar')}
                </Button>
            </Tooltip>
            <Button
                size="sm"
                variant="solid"
                icon={<HiOutlineCheck />}
                disabled={!canChangeStatus}
                loading={loading}
                onClick={() => onSolve?.(CLOSED)}
                className={solveBtnClass}
                block={block}
            >
                {ctaSolveLabel}
            </Button>
        </div>
    )
}

/* --------------------------------------------------------------------------
 * deriveCtaProps — calcula isInformative / noResponsible / ctaSolveLabel / isLegacy
 * a partir del ticket. Lo usan tanto el header consolidado como la mini-bar
 * sticky para no duplicar la heuristica.
 *
 * Clasificacion ESTRICTA por fields backend (no por incidence ni por origin):
 *   - notification: ticket_category_secondary.type === 'notification'
 *   - action:       type === 'action' | question_group_secondary.action === 'resolve'|'reassign'
 *   - legacy:       ninguno de los anteriores -> isLegacy=true, ocultar CTAs
 *
 * incidence/parent_id/originKey NO clasifican: un ticket con incidence=true pero
 * type=null es legacy y no debe exponer botones. Hace falta clasificacion
 * explicita en backend (categoria o cuestionario) para mostrar resolve/close.
 * -------------------------------------------------------------------------- */
export const deriveCtaContext = (data, t, originKey) => {
    const incidence  = data?.incidence
    const parent_id  = data?.parent_id
    const operator   = data?.props?.operator
    const incidents  = data?.props?.incidents
    const ticketCategorySecondary = data?.props?.ticket_category_secondary

    const categoryType        = ticketCategorySecondary?.type
    const questionnaireAction = ticketCategorySecondary?.question_group_secondary?.action

    const hasIncidents  = Array.isArray(incidents) && incidents.length > 0
    const isIncidence   = Boolean(incidence) || hasIncidents

    const isNotification = categoryType === 'notification'
    const isAction       = categoryType === 'action'
        || questionnaireAction === 'resolve'
        || questionnaireAction === 'reassign'

    const isLegacy      = !isNotification && !isAction
    const isInformative = isNotification
    const noResponsible = !operator && (isIncidence || parent_id)

    const ctaSolveLabel = (questionnaireAction === 'resolve' || categoryType === 'action')
        ? t('solve', 'Solucionar')
        : t('close', 'Cerrar')

    return { isInformative, noResponsible, ctaSolveLabel, isLegacy }
}
